import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchStudies,
  fetchAPI,
} from "utils/api";
import { formatDateUTC } from "../../../utils/formatDateTime";
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import ParticipantConfirmationSummary from "../../../components/participant/ParticipantConfirmationSummary";
import { createPatient, fetchPatientById } from "../../../utils/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";
dayjs.extend(utc);

const ConfirmPatientInfo = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const flow = state?.flow || "schedule";
  const returnTo = state?.returnTo || BIOBANK_ROUTES.PARTICIPANT_SCHEDULING;

  const [firstName, setFirstName] = useState("N/A");
  const [lastName, setLastName] = useState("N/A");
  const [birthDate, setBirthDate] = useState("N/A");
  const [email, setEmail] = useState("N/A");
  const [mobilePhoneNumber, setMobilePhoneNumber] = useState("N/A");
  const [homeAddressLine1, setHomeAddressLine1] = useState("");
  const [homeAddressLine2, setHomeAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [consentLabel, setConsentLabel] = useState("Incomplete");
  const [consentColor, setConsentColor] = useState("#E5B229");
  const [studies, setStudies] = useState([]);
  const [signedStudies, setSignedStudies] = useState([]);
  const [patientData, setPatientData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const loadStudiesAndConsents = useCallback(async (patient) => {
    try {
      const studiesData = await fetchStudies();
      const allStudies = studiesData || [];
      setStudies(allStudies);

      if (!patient || !patient.patientId) {
        setSignedStudies([]);
        setConsentLabel("Incomplete");
        setConsentColor("#E5B229");
        return;
      }

      const eligibility = await fetchAPI(
        `biobank/consent/eligibility/preview?patientId=${patient.patientId}`,
        "GET"
      );
      console.log("🔍 Fetched consent eligibility:", eligibility);

      const effectiveStudies = eligibility?.effectiveActiveStudies || [];
      const effectiveStudyIdsSet = new Set(effectiveStudies.map((s) => s.irbStudy));

      const seen = new Set();
      const studiesWithStatus = allStudies.reduce((acc, study) => {
        if (seen.has(study.irbStudy)) return acc;
        seen.add(study.irbStudy);

        acc.push({
          irbStudy: study.irbStudy,
          studyName: study.studyName || study.irbStudy,
          status: effectiveStudyIdsSet.has(study.irbStudy) ? "active" : "incomplete",
        });
        return acc;
      }, []);

      console.log("🔍 Studies with status:", studiesWithStatus);
      setSignedStudies(studiesWithStatus);

      const hasAnyActiveConsent = studiesWithStatus.some((s) => s.status === "active");
      setConsentLabel(hasAnyActiveConsent ? "Complete" : "Incomplete");
      setConsentColor(hasAnyActiveConsent ? "#389F5A" : "#E5B229");
    } catch (error) {
      console.error("Error loading studies and consents:", error);
      setStudies([]);
      setSignedStudies([]);
      setConsentLabel("Incomplete");
      setConsentColor("#E5B229");
    }
  }, []);

  useEffect(() => {
    const loadPatientDetails = async () => {
      try {
        let matchedPatient = state?.existingPatient ? state : null;
        console.log("Using patient from state:", matchedPatient);

        if (matchedPatient) {
          if (matchedPatient.firstName) setFirstName(matchedPatient.firstName);
          if (matchedPatient.lastName) setLastName(matchedPatient.lastName);
          if (matchedPatient.email) setEmail(matchedPatient.email);
          if (matchedPatient.mobilePhoneNumber) setMobilePhoneNumber(matchedPatient.mobilePhoneNumber);
          if (matchedPatient.homeAddressLine1) setHomeAddressLine1(matchedPatient.homeAddressLine1);
          if (matchedPatient.homeAddressLine2) setHomeAddressLine2(matchedPatient.homeAddressLine2);
          if (matchedPatient.city) setCity(matchedPatient.city);
          if (matchedPatient.state) setAddressState(matchedPatient.state);
          if (matchedPatient.zipCode) setZipCode(matchedPatient.zipCode);

          if (matchedPatient.birthDate) {
            const formatted = formatDateUTC(matchedPatient.birthDate);
            setBirthDate(formatted);
          }

          setPatientData(matchedPatient);
          await loadStudiesAndConsents(matchedPatient);
        } else {
          if (state?.firstName) setFirstName(state.firstName);
          if (state?.lastName) setLastName(state.lastName);
          if (state?.email) setEmail(state.email);
          if (state?.mobilePhoneNumber) setMobilePhoneNumber(state.mobilePhoneNumber);
          if (state?.homeAddressLine1) setHomeAddressLine1(state.homeAddressLine1);
          if (state?.homeAddressLine2) setHomeAddressLine2(state.homeAddressLine2);
          if (state?.city) setCity(state.city);
          if (state?.state) setAddressState(state.state);
          if (state?.zipCode) setZipCode(state.zipCode);

          if (state?.birthDate) {
            const formatted = formatDateUTC(state.birthDate);
            setBirthDate(formatted);
          }

          setConsentLabel("Incomplete");
          setConsentColor("#E5B229");
        }
      } catch (err) {
        console.error("Failed to load patient data:", err);
      }
    };

    loadPatientDetails();
  }, [state, loadStudiesAndConsents]);

  const handleContinue = async () => {
    try {
      setErrorMsg("");
      let existingPatient = state?.existingPatient ? state : null;

      let patientData;

      if (existingPatient) {
        console.log("✅ Patient already exists:", existingPatient);
        patientData = {
          ...existingPatient,
          consentStatus: consentLabel,
        };
      } else {
        const patientPayload = {
          firstName,
          lastName,
          email,
          birthDate: state?.birthDate || birthDate,
          patientAccountNumber: state?.matchedEhr,
          mobilePhoneNumber,
          homeAddressLine1: homeAddressLine1 || undefined,
          homeAddressLine2: homeAddressLine2 || undefined,
          city: city || undefined,
          state: addressState || undefined,
          zipCode: zipCode || undefined,
        };

        console.log(" Creating patient with payload:", patientPayload);

        const createdPatient = await createPatient(patientPayload);

        if (!createdPatient || !createdPatient.patientId) {
          throw new Error("Participant creation failed or returned no ID");
        }

        let confirmedPatient = createdPatient;
        for (let i = 0; i < 3; i++) {
          const found = await fetchPatientById(createdPatient.patientId);
          if (found) {
            confirmedPatient = found;
            break;
          }
          await new Promise((res) => setTimeout(res, 500));
        }

        console.log("✅ Confirmed participant in backend:", confirmedPatient);

        patientData = {
          ...confirmedPatient,
          consentStatus: consentLabel,
          flow,
          returnTo,
        };
      }

      navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_ADD_APPOINTMENT, { 
        state: { 
          ...patientData, 
          flow, 
          returnTo,
          isDropIn: state?.isDropIn || false
        } 
      });
    } catch (error) {
      console.error("❌ Error while continuing patient flow:", error);
      setErrorMsg(error?.message || "Failed to process patient. Please try again.");
    }
  };

  const handleBack = () => {
    if (state?.existingPatient) {
      navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_VERIFY, { state: { flow, returnTo } });
    } else {
      navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_ADD, { state: { ...state, flow, returnTo } });
    }
  };

  const activeStudies = signedStudies.filter((s) => s.status === "active");
  const incompleteStudies = signedStudies.filter(
    (s) => s.status === "incomplete"
  );
  const hasNoStudies = signedStudies.length === 0;

  return (
    <FocusedFlowPage
      title="Confirm Participant Information"
      subtitle="Please review the following participant information to verify you are adding the correct participant to the schedule."
      onClose={() => navigate(returnTo)}
      maxWidth={760}
      alert={
        errorMsg ? (
          <Alert severity="error" sx={{ mb: 2, mx: "auto", maxWidth: "500px" }}>
            {errorMsg}
          </Alert>
        ) : state?.existingPatient ? (
          <Alert
            icon={<CheckCircleIcon sx={{ color: "white" }} />}
            severity="success"
            sx={{
              backgroundColor: "#2e7d32",
              borderRadius: 1,
              color: "#e8f5e9",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              padding: "10px 18px",
              fontWeight: 600,
              fontSize: "15.5px",
              maxWidth: "500px",
              mx: "auto",
              justifyContent: "center",
            }}
          >
            Existing Participant Medical Record Number Found!
          </Alert>
        ) : null
      }
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={handleBack}
          sx={{
            borderRadius: 0,
            border: "2px solid #B6862C",
            color: "var(--tamu-maroon)",
            fontWeight: "bold",
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#fdf6e3",
              borderColor: "#B6862C",
            },
          }}
        >
          ← BACK
        </Button>,
        <Button
          key="continue"
          variant="contained"
          onClick={handleContinue}
          sx={{
            backgroundColor: "var(--tamu-maroon)",
            borderRadius: 0,
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#3c0000",
            },
          }}
        >
          CONTINUE →
        </Button>,
      ]}
    >
      <ParticipantConfirmationSummary
        matchedEhr={state?.matchedEhr}
        existingPatient={state?.existingPatient}
        email={email}
        firstName={firstName}
        lastName={lastName}
        birthDate={birthDate}
        mobilePhoneNumber={mobilePhoneNumber}
        homeAddressLine1={homeAddressLine1}
        homeAddressLine2={homeAddressLine2}
        city={city}
        addressState={addressState}
        zipCode={zipCode}
        hasNoStudies={hasNoStudies}
        activeStudies={activeStudies}
        incompleteStudies={incompleteStudies}
      />
    </FocusedFlowPage>
  );
};

export default ConfirmPatientInfo;